// src/redux/api/documentsThunks.ts
import {createAsyncThunk} from '@reduxjs/toolkit';
import {
    DOCUMENT_TYPES_ENDPOINT,
    DOCUMENTS_ENDPOINT,
    PROPERTY_TYPES_ENDPOINT
} from '../../../services/utils/api-consts';
import {
    CreateDocumentRequest,
    CreateDocumentResponse,
    Document,
    DocumentsState, PropertyType,
    DocumentType, Detail, VerificationView
} from "../../../types";
import {deleteData, fetchData, patchData, postData} from "../../../services/data-service";
import {RootState} from '../../store';
import {setOperation} from "../operations/operationsSlice";
import {pollOperationStatus} from "../operations/operationsThunks";
import {fetchPaginatedData} from "../../../services/utils/helpers";

// Тип ответов сервера на пагинацию

// 1) Загрузка списка документов (Light) с пагинацией
//    Сервер возвращает PaginatedResponse<DocumentLight>
export const fetchDocuments = createAsyncThunk<
    Record<string, Document>, // возвращаем словарь
    void,
    { state: { documents: DocumentsState }, rejectValue: string }
>(
    'documents/fetchDocuments',
    async (_, { rejectWithValue }) => {
        try {
            const pageSize = 30;
            const documentsMap: Record<string, Document> = {};

            await fetchPaginatedData<any>(
                DOCUMENTS_ENDPOINT,
                pageSize,
                (items) => {
                    items.forEach(docLight => {
                        documentsMap[docLight.id] = {
                            id: docLight.id,
                            title: docLight.title,
                            description: docLight.description ?? null,
                            documentTypeId: docLight.documentTypeId,
                            documentTypeCode: docLight.documentTypeCode,
                            createdAt: docLight.createdAt,
                            updatedAt: docLight.updatedAt,
                            isLocked: docLight.isLocked,
                            lockedByUserId: docLight.lockedByUserId ?? null,
                            lockedAt: docLight.lockedAt ?? null,
                            s3Key: null,          // Для полной версии пока неизвестно
                            properties: [],
                            details: docLight.details ?? [],
                            detailsLoaded: false, // Флаг: загружена только "лайт" версия
                        };
                    });
                }
            );

            return documentsMap;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// 2) Загрузка полной версии документа (DocumentFull)
export const fetchDocumentDetails = createAsyncThunk<
    Document,
    string, // documentId
    { state: RootState; rejectValue: string }
>(
    'documents/fetchDocumentDetails',
    async (documentId, { getState, rejectWithValue }) => {
        try {
            // Если уже загружен detailsLoaded — возвратим existing
            const existing = getState().documents.documents[documentId];
            if (existing && existing.detailsLoaded) {
                // Прерываем, чтобы не делать повторный запрос
                throw new Error('DETAILS_ALREADY_LOADED');
            }

            // Запрашиваем полную версию
            const url = `${DOCUMENTS_ENDPOINT}${documentId}`;
            const fullDocFromServer = await fetchData<any>(url);
            const doc: Document = {
                id: fullDocFromServer.id,
                title: fullDocFromServer.title,
                description: fullDocFromServer.description ?? null,
                documentTypeId: fullDocFromServer.documentTypeId,
                documentTypeCode: fullDocFromServer.documentTypeCode,
                createdAt: fullDocFromServer.createdAt,
                updatedAt: fullDocFromServer.updatedAt,
                s3Key: fullDocFromServer.s3Key ?? null,
                isLocked: fullDocFromServer.isLocked,
                lockedByUserId: fullDocFromServer.lockedByUserId ?? null,
                lockedAt: fullDocFromServer.lockedAt ?? null,

                // В полной версии свойства уже загружены:
                properties: (fullDocFromServer.properties ?? []).map((p: any) => ({
                    ...p,
                    details: p.details ?? []
                })), // массив PropertyFull
                details: fullDocFromServer.details ?? [],
                detailsLoaded: true
            };

            return doc;

        } catch (error: any) {
            if (error.message === 'DETAILS_ALREADY_LOADED') {
                return rejectWithValue(error.message);
            }
            return rejectWithValue(error.message);
        }
    }
);

// 3) Создание документа (возвращает CreateDocumentResponse = { documentId, operationId, status })
export const createDocument = createAsyncThunk<
    string,                  // возвращаем только documentId
    CreateDocumentRequest,   // аргументы
    { state: RootState; rejectValue: string }
>(
    'documents/createDocument',
    async (docData, { dispatch, rejectWithValue }) => {
        try {
            // 1) Создаём документ
            const response = await postData<CreateDocumentResponse, CreateDocumentRequest>(
                DOCUMENTS_ENDPOINT,
                docData
            );
            // response = { documentId, operationId, status }

            // 2) Сохраняем операцию (ранее было `dispatch(setOperation({...}))`):
            dispatch(
                setOperation({
                    id: response.operationId,
                    type: 'CREATE_DOCUMENT',
                    status: 'InProgress',
                    progress: 0,
                    errorMessage: null,
                    createdAt: new Date().toISOString(),
                    completedAt: null,

                    // В meta кладём, какой документ был создан
                    meta: { documentId: response.documentId },
                })
            );

            // 3) Запускаем polling
            dispatch(pollOperationStatus(response.operationId));

            // 4) Возвращаем documentId (для всяких целей, если нужно)
            return response.documentId;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// 4) Удаление документа
export const deleteDocument = createAsyncThunk<
    string, // возвращаем id документа
    string, // аргумент: documentId
    { rejectValue: string }
>(
    'documents/deleteDocument',
    async (documentId, { rejectWithValue }) => {
        try {
            await deleteData(`${DOCUMENTS_ENDPOINT}${documentId}`);
            return documentId;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// 5) Обновление деталей документа
export const patchDocumentDetails = createAsyncThunk<
    { documentId: string; details: Detail[] },
    { documentId: string; details: { code: string; value: any }[] },
    { rejectValue: string }
>(
    'documents/patchDocumentDetails',
    async ({ documentId, details }, { rejectWithValue }) => {
        try {
            const url = `${DOCUMENTS_ENDPOINT}${documentId}/details`;
            const updatedDetails = await patchData<Detail[], any>(url, { details });
            return { documentId, details: updatedDetails };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// 6) Загрузка Verification View
export const fetchVerificationView = createAsyncThunk<
    VerificationView,
    string, // documentId
    { rejectValue: string }
>(
    'documents/fetchVerificationView',
    async (documentId, { rejectWithValue }) => {
        try {
            const url = `${DOCUMENTS_ENDPOINT}${documentId}/verification-view`;
            return await fetchData<VerificationView>(url);
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);


// ======================
// Загрузка DocumentTypes
// ======================
export const fetchDocumentTypes = createAsyncThunk<
    DocumentType[],
    { pageSize?: number },
    { rejectValue: string }
>(
    'documents/fetchDocumentTypes',
    async ({ pageSize = 30 }, { rejectWithValue }) => {
        try {
            const allItems: DocumentType[] = [];
            await fetchPaginatedData<DocumentType>(
                DOCUMENT_TYPES_ENDPOINT,
                pageSize,
                (items) => {
                    allItems.push(...items);
                }
            );
            return allItems;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// ======================
// Загрузка PropertyTypes
// ======================
export const fetchPropertyTypes = createAsyncThunk<
    PropertyType[],
    { pageSize?: number },
    { rejectValue: string }
>(
    'documents/fetchPropertyTypes',
    async ({ pageSize = 30 }, { rejectWithValue }) => {
        try {
            const allItems: PropertyType[] = [];
            await fetchPaginatedData<PropertyType>(
                PROPERTY_TYPES_ENDPOINT,
                pageSize,
                (items) => {
                    allItems.push(...items);
                }
            );
            return allItems;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// 7) Финализация верификации
export const verifyDocument = createAsyncThunk<
    { documentId: string; verificationStatus: string },
    string, // documentId
    { rejectValue: string }
>(
    'documents/verifyDocument',
    async (documentId, { rejectWithValue }) => {
        try {
            const url = `${DOCUMENTS_ENDPOINT}${documentId}/verify`;
            return await postData<any, any>(url, {});
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);
